import { useState, useEffect } from 'react'
import { LuLink2 } from 'react-icons/lu'
import { Tooltip } from '@mui/material'
import { Link } from 'react-router-dom'
import MuiLink from '@mui/material/Link'
import useToken from '../models/useToken'
import useNetwork from '../models/useNetwork'
import { formatNodeAddress } from '../utilities/general'
import Stack from '@mui/material/Stack'
import NameForm from './NameForm'
import TokenSelect from './TokenSelect'
import Typography from '@mui/material/Typography'
import CodeBlockInstall from './CodeBlockInstall'
import AddThisPhone from './AddThisPhone'
import { useOrgRole } from '../hooks/useOrgRole'
import { callTool } from '../lib/tools'
import { KEY_FORBIDDEN, installChoice, readKeySecret } from '../utilities/enrollmentKey'

interface Props {
  networkId?: string
  initialName?: string
}

export default function AddNode({ networkId, initialName = '' }: Readonly<Props>) {
  const { tokens } = useToken()
  const { networks } = useNetwork()

  const availableNetworks = networks.filter(n => (networkId ? networkId === n.id : true))
  const availableTokens = tokens.filter(t => availableNetworks.some(n => n.id === t.networkId && t.enabled))
  const defaultToken = networkId ? availableTokens.find(t => t.networkId === networkId) : availableTokens[0]

  const [name, setName] = useState<string>(initialName)
  const [selected, setSelected] = useState<Token | undefined>(defaultToken)
  const selectedNetwork = availableNetworks.find(n => n.id === selected?.networkId)

  useEffect(() => {
    if (!selected && defaultToken) setSelected(defaultToken)
  }, [defaultToken, selected])

  useEffect(() => {
    setName(initialName)
  }, [initialName])

  // ⚠⚠ THE INSTALL COMMAND CARRIES NO KEY AND NO NAME BY DEFAULT (Benoit, 2026-10-21). With no
  // NOBGP_KEY the installer runs `nobgp register`, which prints a URL with a code in it
  // (`https://r.nobgp.com/<code>`) and its QR code, and opens it in a browser where there is one.
  // The person signs in there, chooses the network and names the node. So the dialog asks for
  // nothing first, and no key is read. The same command works for every member (app#379).
  // ⚠ The one name it carries is the Restore Node link's (`?name=`, ModalNode): that link puts a
  // removed node back under its old name.
  //
  // The enrollment key stays available, behind "Use an enrollment key instead": a container, a
  // Compose file or an env file has no terminal to show the URL on, and an unattended install
  // needs the key. It is offered only to who may read a key (the network's owner, or an Owner or
  // Admin of its org, docs/PERMISSIONS.md § 5 Network), and the key is read only when that view
  // is open, because each read writes an audit row (`network.key_read`).
  const { controls } = useOrgRole()
  // The keys of the networks this reader controls. The key view opens on one of them.
  const ownKeys = availableTokens.filter(t => controls(availableNetworks.find(n => n.id === t.networkId)?.ownerId))
  const mayUseAKey = ownKeys.length > 0
  const [withKey, setWithKey] = useState(false)
  const mayReadKey = withKey && controls(selectedNetwork?.ownerId)
  // `note` is the sentence for a read that gave no secret: the router refused it, or it failed.
  const [key, setKey] = useState<{ tokenId: string; secret: string | null; note: string } | null>(null)
  const selectedId = selected?.id
  // ⚠ A key that was read is not read again when the reader closes and opens the key view: each
  // read writes an audit row.
  const readFor = key?.secret ? key.tokenId : undefined
  useEffect(() => {
    if (!mayReadKey || !selectedId || readFor === selectedId) return
    let current = true
    void readKeySecret(callTool, selectedId).then(result => {
      if (!current) return
      if (result.kind === 'secret') setKey({ tokenId: selectedId, secret: result.secret, note: '' })
      else setKey({ tokenId: selectedId, secret: null, note: result.kind === 'forbidden' ? KEY_FORBIDDEN : result.message })
    })
    return () => {
      current = false
    }
  }, [mayReadKey, selectedId, readFor])
  // Outside the key view `mayReadKey` is false, so this is the keyless command.
  const { keyless, secret } = installChoice(mayReadKey, selectedId, key)
  const openKeyView = () => {
    if (!controls(selectedNetwork?.ownerId) && ownKeys[0]) setSelected(ownKeys[0])
    setWithKey(true)
  }
  // ⚠ The hidden key selection must not choose the compute node's network: outside the key view
  // the reader did not choose it.
  const computeNetworkId = withKey ? selected?.networkId : networkId

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          mb: 3
        }}>
        <Typography variant="subtitle1" color="primary" sx={{
          flexGrow: 1
        }}>
          New Node
        </Typography>
        {withKey && (
          <Tooltip title="DNS name" placement="top" arrow>
            <Stack direction="row" spacing={1} sx={{
              alignItems: 'center'
            }}>
              <LuLink2 />
              <Typography variant="code">{formatNodeAddress({ name: name || '<hostname>' }, selectedNetwork)}</Typography>
            </Stack>
          </Tooltip>
        )}
      </Stack>
      {!withKey && <AddThisPhone />}
      {withKey ? (
        <>
          {availableTokens.length > 1 && (
            <TokenSelect selected={selected} onSelect={setSelected} networks={availableNetworks} tokens={availableTokens} />
          )}
          <NameForm
            lowercase
            label="Node Name"
            value={name}
            placeholder="<hostname>"
            helperText="Leave blank to use the machine hostname."
            onSubmit={setName}
            submitOnValid
            sx={{ marginBottom: 2 }}
          />
        </>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Run this on the machine. It shows a link and a QR code: open it, sign in, choose the network and name
          the node.
        </Typography>
      )}
      {/* The other way to add a node: the router starts a machine for you (app#330). This is how
          the dialog is reached from the graph, whose Add button opens this one. */}
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        No machine to install on?{' '}
        <MuiLink component={Link} to={computeNetworkId ? `/compute/${computeNetworkId}` : '/compute'}>
          Start a compute node
        </MuiLink>
      </Typography>
      {/* In the key view a command with no key means no key was given: this reader does not
          control the selected network, or the read failed. The command still works. */}
      {withKey && keyless && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {mayReadKey ? key?.note : KEY_FORBIDDEN} This command has no key: it shows a link
          on the machine, where you sign in and choose the network.
        </Typography>
      )}
      <CodeBlockInstall
        color="primary"
        disabled={withKey && (!selected || (!keyless && !secret))}
        name={withKey ? name : initialName}
        network={selectedNetwork}
        keyless={keyless}
        secret={secret ?? undefined}
      />
      {/* ⚠ `withKey` too: a key removed while the view is open must not leave the reader in it. */}
      {(mayUseAKey || withKey) && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          <MuiLink component="button" type="button" onClick={() => (withKey ? setWithKey(false) : openKeyView())}>
            {withKey ? 'Install without a key' : 'Use an enrollment key instead'}
          </MuiLink>
        </Typography>
      )}
    </>
  )
}
